import useAppLanguage, { localize } from './i18n';
import { readPermissionDraft } from './utils/permissionDraft';
import RegistrationDraftProvider from './components/RegistrationDraftProvider';
import useNavigate from './hooks/useSafeNavigate';
import { registerWithEarlyResult } from './utils/earlyRegistration';
import { journey, observeEntry, pageJourneys } from './utils/productJourneys';
import { trackProduct, flushProductEvents } from './utils/productAnalytics';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Box, GlobalStyles } from '@mui/material';
import HomePage from './pages/HomePage';
import MedicationListPage from './pages/MedicationListPage';
import MedicationFormPage from './pages/MedicationFormPage';
import MedicationBundleFormPage from './pages/MedicationBundleFormPage';
import CalendarPage from './pages/CalendarPage';
import SettingsPage from './pages/SettingsPage';
import StockSettingsPage from './pages/StockSettingsPage';
import PrnPage from './pages/PrnPage';
import PrnOnboardingPage from './pages/PrnOnboardingPage';
import SymptomPage from './pages/SymptomPage';
import GuardianPage from './pages/GuardianPage';
import FamilyCareSetupPage from './pages/FamilyCareSetupPage';
import InvitePage from './pages/InvitePage';
import ChatPage from './pages/ChatPage';
import PrivacyPage from './pages/PrivacyPage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import SupportPage from './pages/SupportPage';
import HealthDataPage from './pages/HealthDataPage';
import AdminPage from './pages/AdminPage';
import OnboardingPage from './pages/OnboardingPage';
import CharacterPreviewPage from './pages/CharacterPreviewPage';
import InstallGuidePage from './pages/InstallGuidePage';
import BottomNav from './components/BottomNav';
import PageLoading from './components/PageLoading';
import ConnectionRetryState from './components/ConnectionRetryState';
import FullScreenIntentPermissionPrompt from './components/FullScreenIntentPermissionPrompt';
import ExternalUrlErrorToast from './components/ExternalUrlErrorToast';
import WebviewMessenger from './utils/WebviewMessenger';
import { reconcilePurchasedEntitlements } from './utils/adRequests';
import { getDeviceUuid, registerDevice, updatePushToken, markWidgetEnabled, recordPageDuration } from './api';
import { trackEvent, trackPageView } from './firebase';

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function MedicationNewRedirect() {
    const location = useLocation();
    const params = new URLSearchParams(location.search || '');
    const frequency = params.get('frequency') === 'AS_NEEDED' ? '&frequency=AS_NEEDED' : '';
    return <Navigate to={`/medications/new-bundle?mode=manual${frequency}`} replace />;
}

function normalizePagePath(path) {
    if (/^\/medications\/[^/]+\/edit$/.test(path)) return '/medications/:id/edit';
    if (path === '/my-guardians') return '/guardian';
    return path;
}

function shouldRecordPageDuration(path) {
    return !['/admin', '/install', '/privacy', '/account-deletion', '/support', '/health-data', '/invite'].includes(path);
}

function isPrivateStatsPath(path) {
    return /^\/stats(?:\/|$)/.test(path) || /^\/stats-weekly(?:\/|$)/.test(path);
}

function PageTracker() {
    const { pathname } = useLocation();
    useEffect(() => {
        void flushProductEvents();
        if (isPrivateStatsPath(pathname)) return;
        const feature = { '/settings/stock': 'stock', '/symptoms': 'symptom', '/family-care': 'family', '/guardian': 'family', '/my-guardians': 'family', '/chat': 'ai', '/prn': 'prn', '/medications/new-bundle': 'medication' }[pathname];
        if (feature) trackProduct('feature_view', { feature, source: 'menu' });
        if (pageJourneys[pathname]) journey(pageJourneys[pathname], 'view', 'menu');
    }, [pathname]);
    const currentPageRef = useRef(null);
    const enteredAtRef = useRef(null);
    const sentKeyRef = useRef(null);

    const flushDuration = useCallback((reason) => {
        const pagePath = currentPageRef.current;
        const enteredAt = enteredAtRef.current;
        if (!pagePath || !enteredAt) return;

        const durationMs = Date.now() - enteredAt;
        if (durationMs < 1000) return;

        const sentKey = `${pagePath}:${enteredAt}`;
        if (sentKeyRef.current === sentKey) return;
        sentKeyRef.current = sentKey;

        trackEvent('page_duration', {
            page_path: pagePath,
            page_route: normalizePagePath(pagePath),
            duration_sec: Math.round(durationMs / 1000),
            duration_ms: durationMs,
            end_reason: reason,
        });

        const uuid = localStorage.getItem('pill_reminder_uuid');
        if (uuid && shouldRecordPageDuration(pagePath)) {
            recordPageDuration(uuid, {
                pagePath,
                pageRoute: normalizePagePath(pagePath),
                durationMs,
                endReason: reason,
            }).catch(() => {});
        }
    }, []);

    useEffect(() => {
        flushDuration('route_change');

        if (isPrivateStatsPath(pathname)) {
            currentPageRef.current = null;
            enteredAtRef.current = null;
            return;
        }
        currentPageRef.current = pathname;
        enteredAtRef.current = Date.now();
        trackPageView(pathname);
    }, [flushDuration, pathname]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flushDuration('hidden');
                enteredAtRef.current = null;
                return;
            }

            if (document.visibilityState === 'visible') {
                enteredAtRef.current = Date.now();
            }
        };

        const handlePageHide = () => {
            flushDuration('pagehide');
            enteredAtRef.current = null;
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            flushDuration('unmount');
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [flushDuration]);

    return null;
}

function ExposeNavigate() {
    const navigate = useNavigate();
    useEffect(() => {
        window.__NAVIGATE = (path) => navigate(path);
        return () => { delete window.__NAVIGATE; };
    }, [navigate]);
    return null;
}

function OnboardingRedirect() {
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        if (location.pathname === '/' && !localStorage.getItem('onboarding_done')) {
            // registerDevice가 완료될 시간을 준 후 리다이렉트
            const timer = setTimeout(() => navigate('/onboarding', { replace: true }), 500);
            return () => clearTimeout(timer);
        }
    }, [location.pathname, navigate]);
    return null;
}

const WEB_PUBLIC_PATHS = ['/install', '/invite', '/privacy', '/account-deletion', '/support', '/health-data', '/admin'];
const HIDE_NAV_PATTERNS = ['/new', '/edit', '/onboarding', '/invite'];
const HIDE_NAV_PATHS = ['/chat', '/install', '/admin', '/account-deletion', '/support', '/health-data'];
const WEB_DEV_SESSION_KEY = 'pill_reminder_web_dev';
const APP_RUNTIME_INFO_KEY = 'pill_reminder_app_runtime_info';
const NATIVE_ACCESS_PROBE_MS = 700;
const DEVICE_RUNTIME_REGISTER_MIN_INTERVAL_MS = 10 * 60 * 1000;
const PUSH_TOKEN_REGISTER_MIN_INTERVAL_MS = 10 * 60 * 1000;

function stableRuntimeSignature(data = {}) {
    const payload = {
        pushToken: data.pushToken || null,
        fcmToken: data.fcmToken || null,
        platform: data.platform || null,
        deviceModel: data.deviceModel || null,
        appVersion: data.appVersion || null,
        runtimeVersion: data.runtimeVersion || null,
        channel: data.channel || null,
        osVersion: data.osVersion || null,
        store: data.store || null,
        locale: data.locale || null,
        timeZone: data.timeZone || null,
    };
    return JSON.stringify(payload);
}

function isWebDevMode(search) {
    const hasDevParam = new URLSearchParams(search).has('dev');
    if (hasDevParam) {
        sessionStorage.setItem(WEB_DEV_SESSION_KEY, '1');
        return true;
    }
    return sessionStorage.getItem(WEB_DEV_SESSION_KEY) === '1';
}

function App() {
    useAppLanguage();
    const recovery = useRef(undefined);

    const location = useLocation();
    const [deviceReady, setDeviceReady] = useState(false);
    const [deviceRegistered, setDeviceRegistered] = useState(false);
    const [registrationFailed, setRegistrationFailed] = useState(false);
    const [registrationAttempt, setRegistrationAttempt] = useState(0);
    const isDevMode = isWebDevMode(location.search);
    const [nativeProbeDone, setNativeProbeDone] = useState(() =>
        Boolean(window.IS_NATIVE_APP || window.ReactNativeWebView || isDevMode || (WEB_PUBLIC_PATHS.includes(window.location.pathname) && window.location.pathname !== '/install'))
    );
    const isAppAccess = Boolean(window.IS_NATIVE_APP || window.ReactNativeWebView || isDevMode);
    const isAdminPage = location.pathname === '/admin';
    const usesDeviceContext = isAppAccess && !isAdminPage && !['/account-deletion', '/health-data'].includes(location.pathname);
    if (usesDeviceContext && recovery.current === undefined) {
        recovery.current = readPermissionDraft(getDeviceUuid());
    }
    const isPublicWebPath = WEB_PUBLIC_PATHS.includes(location.pathname);
    const isInstallPage = location.pathname === '/install';
    const hideNav = HIDE_NAV_PATHS.includes(location.pathname)
        || HIDE_NAV_PATTERNS.some(p => location.pathname.includes(p));
    const lastRuntimeRegisterRef = useRef({ signature: '', sentAt: 0 });
    const lastPushTokenRegisterRef = useRef({ signature: '', sentAt: 0 });

    useEffect(() => {
        if (nativeProbeDone) return undefined;
        let frameId = null;
        const startedAt = Date.now();
        const probe = () => {
            if (window.IS_NATIVE_APP || window.ReactNativeWebView || Date.now() - startedAt >= NATIVE_ACCESS_PROBE_MS) {
                setNativeProbeDone(true);
                return;
            }
            frameId = window.requestAnimationFrame(probe);
        };
        frameId = window.requestAnimationFrame(probe);
        return () => {
            if (frameId) window.cancelAnimationFrame(frameId);
        };
    }, [nativeProbeDone]);

    useEffect(() => {
        let uuid = null;
        let cancelled = false;
        let registrationPromise = Promise.resolve();
        setRegistrationFailed(false);
        const retryRegistration = () => setRegistrationAttempt(value => value + 1);
        window.addEventListener('online', retryRegistration);

        // Register device on first load — 네이티브 앱 또는 ?dev 일 때만.
        // /admin은 웹 공개 페이지라 UUID 생성 및 디바이스 등록을 하지 않는다.
        if (usesDeviceContext) {
            uuid = getDeviceUuid();
            registrationPromise = registerWithEarlyResult(uuid, registerDevice);
            registrationPromise.then(() => {
                if (cancelled) return;
                setDeviceRegistered(true);
                setDeviceReady(true);
                WebviewMessenger.send({ type: 'DEVICE_REGISTERED', data: { uuid } });
            }).catch(() => { if (!cancelled) setRegistrationFailed(true); });
        } else {
            setDeviceReady(true);
        }

        let pendingPushToken = null;
        let pushTokenSaving = false;
        let pushTokenRetryTimer = null;
        const persistPushToken = async () => {
            if (cancelled || pushTokenSaving || !pendingPushToken || !uuid) return;
            const current = pendingPushToken;
            pushTokenSaving = true;
            try {
                await registrationPromise;
                if (cancelled) return;
                await updatePushToken(uuid, current.data.pushToken, current.data.platform,
                    current.data.fcmToken, current.data.deviceModel, current.data.appVersion, current.data);
                lastPushTokenRegisterRef.current = { signature: current.signature, sentAt: Date.now() };
                if (pendingPushToken === current) pendingPushToken = null;
            } catch (err) {
                console.error('Push token registration failed:', err);
            } finally {
                pushTokenSaving = false;
                if (!cancelled && pendingPushToken) {
                    pushTokenRetryTimer = setTimeout(persistPushToken, 5000);
                }
            }
        };

        // Listen for messages from native app
        const cleanup = WebviewMessenger.onMessage((message) => {
            const { type, data } = message;
            if (type === 'AD_FREE_PURCHASE_RESULT' && message.status === 'success') {
                void reconcilePurchasedEntitlements(message);
            }

            if (type === 'PUSH_TOKEN_OBTAINED') {
                if (!uuid) return;
                const signature = stableRuntimeSignature(data);
                const now = Date.now();
                const last = lastPushTokenRegisterRef.current;
                if (last.signature === signature && now - last.sentAt < PUSH_TOKEN_REGISTER_MIN_INTERVAL_MS) return;
                pendingPushToken = { signature, data };
                clearTimeout(pushTokenRetryTimer);
                persistPushToken();
            }

            if (type === 'APP_RUNTIME_INFO') {
                try {
                    if (Number.isInteger(message.webviewSession)) window.__NATIVE_WEBVIEW_SESSION = message.webviewSession;
                    const runtimeInfo = {
                        ...data,
                        receivedAt: new Date().toISOString(),
                    };
                    window.__APP_RUNTIME_INFO = runtimeInfo;
                    localStorage.setItem(APP_RUNTIME_INFO_KEY, JSON.stringify(runtimeInfo));
                    window.dispatchEvent(new CustomEvent('app-runtime-info-updated', { detail: runtimeInfo }));
                    if (message.reason === 'app_foreground') {
                        window.dispatchEvent(new CustomEvent('native-app-foreground', { detail: runtimeInfo }));
                    }
                    if (uuid) {
                        const signature = stableRuntimeSignature(data);
                        const now = Date.now();
                        const last = lastRuntimeRegisterRef.current;
                        if (last.signature === signature && now - last.sentAt < DEVICE_RUNTIME_REGISTER_MIN_INTERVAL_MS) return;
                        lastRuntimeRegisterRef.current = { signature, sentAt: now };
                        registerDevice(uuid, null, data.platform, null, null, data.appVersion, data).catch(() => {});
                    }
                } catch {}
            }

            if (type === 'NAVIGATE_TO_LOG') {
                observeEntry('notification');
                if (window.__NAVIGATE) { window.__NAVIGATE('/'); }
                else { window.location.href = '/'; }
            }

            // 위젯 탭으로 진입한 사용자 — 위젯 사용 기기로 1회 마킹.
            // 백엔드 markWidgetEnabled는 이미 true면 NOOP이라 중복 호출 무해하지만 세션 가드로 호출 줄임.
            if (type === 'WIDGET_OPENED') {
                observeEntry('widget');
                if (!uuid) return;
                if (!sessionStorage.getItem('widget_reported')) {
                    sessionStorage.setItem('widget_reported', '1');
                    markWidgetEnabled(uuid).catch(() => {});
                }
                if (window.__NAVIGATE) { window.__NAVIGATE('/'); }
                else { window.location.href = '/'; }
            }
        });

        return () => {
            cancelled = true;
            clearTimeout(pushTokenRetryTimer);
            cleanup();
            window.removeEventListener('online', retryRegistration);
        };
    }, [usesDeviceContext, registrationAttempt]);

    if ((!nativeProbeDone && (!isPublicWebPath || isInstallPage)) || !deviceReady || (usesDeviceContext && !deviceRegistered)) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                {registrationFailed ? <ConnectionRetryState fullScreen title={localize("앱을 준비하지 못했어요")} onRetry={() => setRegistrationAttempt(value => value + 1)} /> : <PageLoading />}
            </Box>
        );
    }

    if (recovery.current) {
        const draft = recovery.current;
        recovery.current = null;
        if (window.ReactNativeWebView && draft.uuid === getDeviceUuid() && location.pathname === '/') return <Navigate to={draft.path} state={{ permissionRecovery: true }} replace />;
    }

    if (isAppAccess && isInstallPage) {
        return <Navigate to="/" replace />;
    }

    if (!isAppAccess && !isPublicWebPath) {
        return <Navigate to="/install" replace />;
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#e5e7eb',
            display: 'flex',
            justifyContent: 'center',
        }}>
            <GlobalStyles styles={{ '#root': { maxWidth: isAdminPage ? 1120 : 480 } }} />
            <Box sx={{
                width: '100%',
                maxWidth: isAdminPage ? 1120 : 480,
                // pb 72(BottomNav 자리)와 누적되어 element가 항상 viewport 초과하던 문제 회피.
                // hideNav 아닐 때 minHeight를 viewport - 72로 줄여 element 정확히 100vh.
                minHeight: hideNav ? '100vh' : 'calc(100vh - 72px)',
                bgcolor: '#f5f5f5',
                pb: hideNav ? 0 : '72px',
                position: 'relative',
                boxShadow: '0 0 24px rgba(0,0,0,0.06)',
            }}>
                <ScrollToTop />
                {!['/health-data', '/account-deletion'].includes(location.pathname) && <PageTracker />}
                <ExposeNavigate />
                <ExternalUrlErrorToast />
                {usesDeviceContext && <OnboardingRedirect />}
                <RegistrationDraftProvider enabled={usesDeviceContext}><Routes>
                    <Route path="/install" element={<InstallGuidePage />} />
                    <Route path="/onboarding" element={<OnboardingPage />} />
                    <Route path="/" element={<HomePage />} />
                    <Route path="/medications" element={<MedicationListPage />} />
                    <Route path="/medications/new/select" element={<MedicationNewRedirect />} />
                    <Route path="/medications/new-bundle" element={<MedicationBundleFormPage />} />
                    <Route path="/medications/new" element={<MedicationNewRedirect />} />
                    <Route path="/medications/:id/edit" element={<MedicationFormPage />} />
                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/settings/stock" element={<StockSettingsPage />} />
                    {/* Statistics are private; old links also return to home. */}
                    <Route path="/stats/*" element={<Navigate to="/" replace />} />
                    <Route path="/stats-weekly/*" element={<Navigate to="/" replace />} />
                    <Route path="/prn" element={<PrnPage />} />
                    <Route path="/prn/onboarding" element={<PrnOnboardingPage />} />
                    <Route path="/symptoms" element={<SymptomPage />} />
                    <Route path="/guardian" element={<GuardianPage />} />
                    <Route path="/my-guardians" element={<GuardianPage />} />
                    <Route path="/family-care" element={<FamilyCareSetupPage />} />
                    <Route path="/invite" element={<InvitePage />} />
                    <Route path="/chat" element={<ChatPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/account-deletion" element={<AccountDeletionPage />} />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="/health-data" element={<HealthDataPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/character-preview" element={<CharacterPreviewPage />} />
                </Routes></RegistrationDraftProvider>
                {usesDeviceContext && !isInstallPage && <FullScreenIntentPermissionPrompt />}
                {usesDeviceContext && !isInstallPage && <BottomNav />}
            </Box>
        </Box>
    );
}

export default App;
